<template>
  <div class="profile-page">
    <div class="container">
      <h1 class="title">Profile</h1>

      <div class="profile-content">
        <!-- User Info Card -->
        <div class="card-section user-info-card">
          <div class="user-header">
            <div class="avatar-container">
              <img :src="avatar" alt="User Avatar" class="avatar">
            </div>
            <div class="user-details">
              <h2 class="username">{{ username }}</h2>
              <div class="role-tag" :class="getRoleClass(role)">
                <i :class="getRoleIcon(role)"></i>
                <span class="role-name">{{ role }}</span>
              </div>
            </div>
            <button @click="toggleEdit" class="edit-button" :disabled="isLoading">
              <span v-if="isLoading">
                <i class="fas fa-spinner fa-spin"></i> Loading...
              </span>
              <span v-else-if="isEditing">
                <i class="fas fa-save"></i> Save
              </span>
              <span v-else>
                <i class="fas fa-edit"></i> Edit
              </span>
            </button>
          </div>
        </div>

        <!-- Profile Details Card -->
        <div class="card-section profile-details-card">
          <h3>Profile Details</h3>

          <div class="profile-form">
            <div v-for="item in profileItems" :key="item.label" class="form-group">
              <label :for="item.key">{{ item.label }}</label>
              <div v-if="!isEditing" class="field-value">
                {{ item.value || 'Not specified' }}
              </div>
              <input v-else :id="item.key" v-model="item.value" :placeholder="item.label" :type="item.type || 'text'"
                class="form-input">
            </div>
          </div>

          <div v-if="errorMessage" class="message error-message">
            {{ errorMessage }}
          </div>

          <div v-if="successMessage" class="message success-message">
            {{ successMessage }}
          </div>
        </div>

        <!-- System Info Card -->
        <div class="card-section system-info-card">
          <h3>System Information</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">User ID</span>
              <span class="info-value">{{ userId }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Last Login</span>
              <span class="info-value">{{ lastLoginDate }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService';

export default {
  name: 'ProfilePage',
  data() {
    return {
      isEditing: false,
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      username: localStorage.getItem('username'),
      role: localStorage.getItem('role'),
      userId: localStorage.getItem('userId'),
      lastLoginDate: new Date().toLocaleString(),
      profile: {
        email: '',
        phone: '',
        affiliation: '',
        education: ''
      },
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(localStorage.getItem('username'))}&background=random&size=200`,
      profileItems: [
        {
          label: 'Email',
          value: '',
          icon: 'email',
          key: 'email',
          type: 'email'
        },
        {
          label: 'Phone',
          value: '',
          icon: 'phone',
          key: 'phone',
          type: 'tel'
        },
        {
          label: 'Affiliation',
          value: '',
          icon: 'business',
          key: 'affiliation',
          type: 'text'
        },
        {
          label: 'Education',
          value: '',
          icon: 'school',
          key: 'education',
          type: 'text'
        }
      ]
    };
  },
  async mounted() {
    await this.loadProfile();
  },
  methods: {
    async loadProfile() {
      try {
        this.isLoading = true;
        this.errorMessage = '';

        const response = await apiService.fetchUserProfile(this.userId);

        if (response.success && response.profile) {
          this.profile = response.profile;
          // Update profileItems with actual data
          this.profileItems.forEach(item => {
            item.value = this.profile[item.key] || '';
          });
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        this.errorMessage = 'Failed to load profile data';
      } finally {
        this.isLoading = false;
      }
    },

    async saveProfile() {
      try {
        this.isLoading = true;
        this.errorMessage = '';
        this.successMessage = '';

        // Collect data from profileItems
        const profileData = {};
        this.profileItems.forEach(item => {
          profileData[item.key] = item.value || null;
        });

        const response = await apiService.updateUserProfile(this.userId, profileData);

        if (response.success) {
          this.profile = response.profile;
          this.successMessage = 'Profile updated successfully!';
          setTimeout(() => {
            this.successMessage = '';
          }, 3000);
        }
      } catch (error) {
        console.error('Error saving profile:', error);
        this.errorMessage = 'Failed to save profile data';
      } finally {
        this.isLoading = false;
      }
    },

    async toggleEdit() {
      if (this.isEditing) {
        // Save the changes
        await this.saveProfile();
      }
      this.isEditing = !this.isEditing;
    },

    getRoleIcon(role) {
      const roleIcons = {
        'admin': 'fas fa-crown',
        'teacher': 'fas fa-chalkboard-teacher',
        'heilpädagoge': 'fas fa-heart',
        'specialist': 'fas fa-user-tie'
      };
      return roleIcons[role] || 'fas fa-user';
    },

    getRoleClass(role) {
      return `role-${role}`;
    }
  }
};
</script>

<style scoped>
/* Profile Page Styles */
.profile-page {
  background-color: #f5f7fa;
  min-height: 100vh;
  font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  color: #323232;
}

.title {
  color: #323232;
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
  border-bottom: 2px solid #42b983;
  padding-bottom: 0.5rem;
  display: inline-block;
}

.profile-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Card Section Base */
.card-section {
  background-color: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.card-section h3 {
  color: #323232;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  font-weight: 600;
}

/* User Info Card */
.user-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  position: relative;
}

.avatar-container {
  flex-shrink: 0;
}

.avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #42b983;
  box-shadow: 0 2px 8px rgba(66, 185, 131, 0.2);
}

.user-details {
  flex-grow: 1;
}

.username {
  color: #323232;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
}

/* Role Tags */
.role-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 20px;
  border: 1px solid;
  max-width: fit-content;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.role-tag i {
  font-size: 0.875rem;
}

.role-name {
  text-transform: capitalize;
}

/* Role-specific colors */
.role-admin {
  background: #fef7f7;
  border-color: #f56565;
  color: #c53030;
}

.role-teacher {
  background: #f0fff4;
  border-color: #42b983;
  color: #2f855a;
}

.role-heilpädagoge {
  background: #f7faff;
  border-color: #4299e1;
  color: #2b6cb0;
}

.role-specialist {
  background: #faf5ff;
  border-color: #9f7aea;
  color: #6b46c1;
}

/* Edit Button */
.edit-button {
  background-color: #42b983;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  position: absolute;
  top: 0;
  right: 0;
}

.edit-button:hover:not(:disabled) {
  background-color: #369870;
  transform: translateY(-1px);
}

.edit-button:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

/* Profile Form */
.profile-form {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 500;
  color: #505050;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
}

.field-value {
  color: #323232;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  min-height: 1.25rem;
}

.form-input {
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  background: white;
}

.form-input:focus {
  outline: none;
  border-color: #42b983;
  box-shadow: 0 0 0 3px rgba(66, 185, 131, 0.1);
}

/* Messages */
.message {
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  margin-top: 1rem;
}

.error-message {
  background-color: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.success-message {
  background-color: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

/* System Info */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.info-label {
  font-weight: 500;
  color: #6b7280;
  font-size: 0.875rem;
}

.info-value {
  color: #323232;
  font-weight: 500;
  font-size: 0.875rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .container {
    padding: 1rem;
  }

  .user-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .edit-button {
    position: static;
    align-self: flex-end;
  }

  .profile-form {
    grid-template-columns: 1fr;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
}

@media (max-width: 480px) {
  .avatar {
    width: 60px;
    height: 60px;
  }

  .username {
    font-size: 1.25rem;
  }

  .title {
    font-size: 1.5rem;
  }
}
</style>